"use client";

import * as React from "react";
import { datePickerValueToDateOnly } from "@ibada/shared-types";
import { format } from "date-fns";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { useUiDateLocale } from "../../../lib/ui-date-locale";
import { cn } from "../../../lib/utils";
import { Calendar } from "../../ui/calendar";
import { MobileSearchField } from "../../ui/mobile-search-field";
import { useDataTableMobileLabels } from "./data-table-mobile-list";
import {
  parseDateValue,
  parseMultiSelectValue,
  serializeMultiSelectValue,
  type TableFilterFieldConfig,
  type TableFilterSelectOption,
  type TableFilterValues,
} from "./data-table-filter-types";

/** A choice list this short shows as pills in place; a longer one is a row that opens a picker screen. */
const PILL_MAX_OPTIONS = 5;

const ROW = "flex min-h-[3.25rem] w-full min-w-0 items-center gap-3 px-4 text-left text-base";

function normalize(text: string) {
  return text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/** Which screen of the sheet is showing: the list of filters, or one filter's picker. */
type FilterScreen =
  | { kind: "list" }
  | { kind: "options"; fieldId: string }
  | { kind: "date"; fieldId: string; key: string; title: string };

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className='flex flex-col'>
      <h3 className='px-4 pb-1.5 text-sm font-bold text-muted-foreground'>{title}</h3>
      <div className='bg-card divide-border/60 divide-y overflow-hidden rounded-2xl'>{children}</div>
    </section>
  );
}

function Row({ label, value, placeholder, onPress }: { label: string; value?: string; placeholder: string; onPress: () => void }) {
  return (
    <button type='button' onClick={onPress} className={cn(ROW, "active:bg-muted")}>
      <span className='max-w-[55%] shrink-0 truncate font-semibold text-foreground'>{label}</span>
      <span className={cn("min-w-0 flex-1 truncate text-right font-semibold", value ? "text-foreground" : "text-muted-foreground/70")}>
        {value || placeholder}
      </span>
      <ChevronRightIcon aria-hidden className='size-4 shrink-0 text-muted-foreground/50' />
    </button>
  );
}

/** Pills for a short choice (status, yes / no): tap one, tap "all" to clear. */
function Pills({
  options,
  value,
  onChange,
  disabled,
}: {
  options: TableFilterSelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div role='radiogroup' className='flex flex-wrap gap-2'>
      {options.map((option) => {
        const selected = value === option.value || (value.trim() === "" && option.value.trim() === "");
        return (
          <button
            key={option.value || "__empty__"}
            type='button'
            role='radio'
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex h-9 max-w-full items-center rounded-full px-4 text-sm font-bold transition-colors disabled:opacity-50",
              selected ? "bg-primary text-primary-foreground" : "bg-card text-foreground ring-1 ring-border/70 ring-inset",
            )}
          >
            <span className='truncate'>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

type DataTableMobileFilterSheetProps = {
  fields: TableFilterFieldConfig[];
  values: TableFilterValues;
  onChange: (key: string, value: string) => void;
  /** Resets one screen back to the list whenever the sheet opens. */
  open: boolean;
};

/**
 * The filters on a phone, in the app's form style: each filter is a titled group on the sheet's canvas. Short
 * choices are pills in place; long lists, member lookups and dates open a picker screen INSIDE the same sheet
 * (never a second sheet on top), with the app's search field and the shared calendar.
 */
export function DataTableMobileFilterSheet({ fields, values, onChange, open }: DataTableMobileFilterSheetProps) {
  const labels = useDataTableMobileLabels();
  const dateLocale = useUiDateLocale();
  const [screen, setScreen] = React.useState<FilterScreen>({ kind: "list" });
  const [query, setQuery] = React.useState("");

  React.useEffect(() => {
    if (open) setScreen({ kind: "list" });
  }, [open]);

  const formatDay = (raw: string | undefined) => {
    const date = parseDateValue(raw);
    return date ? format(date, "d MMM yyyy", { locale: dateLocale }) : undefined;
  };

  const openOptions = (field: TableFilterFieldConfig) => {
    setQuery("");
    if ((field.type === "lookup" || field.type === "asyncSelect") && field.onSearchValueChange) field.onSearchValueChange("");
    setScreen({ kind: "options", fieldId: field.id });
  };

  // --- A picker screen ------------------------------------------------------------------------------------
  if (screen.kind !== "list") {
    const field = fields.find((candidate) => candidate.id === screen.fieldId);
    const back = () => setScreen({ kind: "list" });
    const header = (title: string) => (
      <div className='grid grid-cols-[1fr_minmax(0,auto)_1fr] items-center gap-2'>
        <button
          type='button'
          onClick={back}
          className='inline-flex items-center gap-0.5 justify-self-start rounded-full py-1 pr-2 text-base font-bold text-primary active:bg-primary/10'
        >
          <ChevronLeftIcon aria-hidden className='size-5' strokeWidth={2.5} />
          {labels.filtersTitle}
        </button>
        <span className='truncate text-base font-extrabold'>{title}</span>
        <span />
      </div>
    );

    if (field && screen.kind === "date") {
      return (
        <div className='flex min-h-0 flex-1 flex-col gap-3 px-4 pb-3'>
          {header(screen.title)}
          <Calendar
            mode='single'
            selected={parseDateValue(values[screen.key])}
            defaultMonth={parseDateValue(values[screen.key])}
            onSelect={(date) => {
              onChange(screen.key, datePickerValueToDateOnly(date) ?? "");
              back();
            }}
            className='mx-auto w-full max-w-sm bg-transparent p-0 [--cell-size:2.75rem]'
          />
          {values[screen.key] ? (
            <button
              type='button'
              onClick={() => {
                onChange(screen.key, "");
                back();
              }}
              className='bg-card mx-auto flex min-h-[3.25rem] w-full max-w-sm items-center rounded-2xl px-4 text-left text-base font-semibold text-muted-foreground active:bg-muted'
            >
              {labels.filtersClear}
            </button>
          ) : null}
        </div>
      );
    }

    if (field && (field.type === "select" || field.type === "lookup" || field.type === "asyncSelect" || field.type === "multiSelect")) {
      const serverSearch = field.type === "lookup" || field.type === "asyncSelect" ? field.onSearchValueChange : undefined;
      const loading = (field.type === "lookup" || field.type === "asyncSelect") && Boolean(field.isLoading);
      const multi = field.type === "multiSelect";
      const selected = new Set(multi ? parseMultiSelectValue(values[field.key] ?? "") : [values[field.key] ?? ""]);
      const needle = normalize(query.trim());
      const options = field.options.filter((option) => option.value.trim() !== "");
      const shown = serverSearch || !needle ? options : options.filter((option) => normalize(option.label).includes(needle));
      const pick = (value: string) => {
        if (!multi) {
          onChange(field.key, value);
          back();
          return;
        }
        const next = new Set(selected);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        const order = new Map(options.map((option, index) => [option.value, index]));
        onChange(field.key, serializeMultiSelectValue(Array.from(next).sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0))));
      };

      return (
        <div className='flex min-h-0 flex-1 flex-col gap-3 px-4 pb-3'>
          {header(field.label)}
          <MobileSearchField
            value={query}
            onValueChange={(next) => {
              setQuery(next);
              serverSearch?.(next);
            }}
            placeholder={labels.filtersSearch}
            clearLabel={labels.clearSearch}
          />
          <div className='min-h-0 flex-1 overflow-y-auto'>
            {shown.length === 0 ? (
              <p className='py-8 text-center text-sm font-semibold text-muted-foreground'>
                {loading ? (
                  <span className='inline-block size-5 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-transparent' />
                ) : (
                  labels.filtersNoResults
                )}
              </p>
            ) : (
              <ul className='bg-card divide-border/60 divide-y overflow-hidden rounded-2xl'>
                {shown.map((option) => (
                  <li key={option.value}>
                    <button
                      type='button'
                      onClick={() => pick(option.value)}
                      className='flex min-h-[3.25rem] w-full items-center gap-3 px-4 py-2 text-left active:bg-muted'
                    >
                      <span className='line-clamp-2 min-w-0 flex-1 text-base font-semibold break-words text-foreground'>{option.label}</span>
                      {selected.has(option.value) ? <CheckIcon aria-hidden className='size-5 shrink-0 text-primary' strokeWidth={3} /> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {/* "Any" is the way back to no filter; hidden while searching so it never reads as a second "no results". */}
            {!needle && (values[field.key] ?? "") !== "" ? (
              <button
                type='button'
                onClick={() => {
                  onChange(field.key, "");
                  back();
                }}
                className='bg-card mt-3 flex min-h-[3.25rem] w-full items-center rounded-2xl px-4 text-left text-base font-semibold text-muted-foreground active:bg-muted'
              >
                {labels.filtersClear}
              </button>
            ) : null}
          </div>
        </div>
      );
    }
  }

  // --- The list of filters ----------------------------------------------------------------------------------
  // Single-value filters (a list, a lookup, a day, a word) are rows of ONE grouped list, labelled by the row.
  // Ranges and short choices are blocks of their own with a title. Consecutive rows share a card.
  type Block = { kind: "row"; key: string; node: React.ReactNode } | { kind: "block"; key: string; node: React.ReactNode };
  const blocks: Block[] = fields.map((field): Block => {
    const disabled = field.disabled === true;

    if (field.type === "dateRange") {
      return {
        kind: "block",
        key: field.id,
        node: (
          <Section title={field.label}>
            <Row
              label={field.fromLabel ?? labels.filtersFrom}
              value={formatDay(values[field.fromKey])}
              placeholder={labels.filtersChoose}
              onPress={() => setScreen({ kind: "date", fieldId: field.id, key: field.fromKey, title: field.fromLabel ?? labels.filtersFrom })}
            />
            <Row
              label={field.toLabel ?? labels.filtersTo}
              value={formatDay(values[field.toKey])}
              placeholder={labels.filtersChoose}
              onPress={() => setScreen({ kind: "date", fieldId: field.id, key: field.toKey, title: field.toLabel ?? labels.filtersTo })}
            />
          </Section>
        ),
      };
    }

    if (field.type === "numberRange") {
      const input = (key: string, label: string) => (
        <label className={ROW}>
          <span className='max-w-[55%] shrink-0 truncate font-semibold text-foreground'>{label}</span>
          <input
            inputMode='decimal'
            value={values[key] ?? ""}
            disabled={disabled}
            placeholder={labels.filtersAny}
            onChange={(event) => onChange(key, event.target.value.replace(",", ".").replace(/[^\d.]/g, ""))}
            className='min-w-0 flex-1 bg-transparent text-right text-base font-semibold text-foreground tabular-nums outline-none placeholder:text-muted-foreground/70'
          />
        </label>
      );
      return {
        kind: "block",
        key: field.id,
        node: (
          <Section title={field.label}>
            {input(field.minKey, field.minLabel ?? labels.filtersMin)}
            {input(field.maxKey, field.maxLabel ?? labels.filtersMax)}
          </Section>
        ),
      };
    }

    const pillOptions: TableFilterSelectOption[] | null =
      field.type === "boolean"
        ? [
            { value: "", label: labels.filtersAny },
            { value: "true", label: field.trueLabel ?? "Yes" },
            { value: "false", label: field.falseLabel ?? "No" },
          ]
        : field.type === "select" && !field.creatable && field.options.length > 1 && field.options.length <= PILL_MAX_OPTIONS
          ? field.options
          : null;
    if (pillOptions && (field.type === "boolean" || field.type === "select")) {
      return {
        kind: "block",
        key: field.id,
        node: (
          <section className='flex flex-col gap-2'>
            <h3 className='px-4 text-sm font-bold text-muted-foreground'>{field.label}</h3>
            <Pills disabled={disabled} options={pillOptions} value={values[field.key] ?? ""} onChange={(next) => onChange(field.key, next)} />
          </section>
        ),
      };
    }

    if (field.type === "dateTime") {
      return {
        kind: "row",
        key: field.id,
        node: (
          <Row
            label={field.label}
            value={formatDay(values[field.key])}
            placeholder={labels.filtersAny}
            onPress={() => setScreen({ kind: "date", fieldId: field.id, key: field.key, title: field.label })}
          />
        ),
      };
    }

    if (field.type === "select" || field.type === "lookup" || field.type === "asyncSelect") {
      const raw = values[field.key] ?? "";
      const label = raw ? field.options.find((option) => option.value === raw)?.label ?? raw : undefined;
      return {
        kind: "row",
        key: field.id,
        node: <Row label={field.label} value={label} placeholder={labels.filtersAny} onPress={() => openOptions(field)} />,
      };
    }

    if (field.type === "multiSelect") {
      const picked = parseMultiSelectValue(values[field.key] ?? "");
      const summary =
        picked.length === 0
          ? undefined
          : picked.length === 1
            ? field.options.find((option) => option.value === picked[0])?.label ?? picked[0]
            : labels.filtersSelectedCount(picked.length);
      return {
        kind: "row",
        key: field.id,
        node: <Row label={field.label} value={summary} placeholder={labels.filtersAny} onPress={() => openOptions(field)} />,
      };
    }

    // Free text: the label on the left, typed on the right.
    return {
      kind: "row",
      key: field.id,
      node: (
        <label className={ROW}>
          <span className='max-w-[55%] shrink-0 truncate font-semibold text-foreground'>{field.label}</span>
          <input
            value={values[field.key] ?? ""}
            disabled={disabled}
            placeholder={field.placeholder ?? labels.filtersAny}
            onChange={(event) => onChange(field.key, event.target.value)}
            className='min-w-0 flex-1 bg-transparent text-right text-base font-semibold text-foreground outline-none placeholder:text-muted-foreground/70'
          />
        </label>
      ),
    };
  });

  const grouped: React.ReactNode[] = [];
  let run: Block[] = [];
  const flush = () => {
    if (run.length === 0) return;
    grouped.push(
      <div key={`rows-${run[0].key}`} className='bg-card divide-border/60 divide-y overflow-hidden rounded-2xl'>
        {run.map((block) => (
          <React.Fragment key={block.key}>{block.node}</React.Fragment>
        ))}
      </div>,
    );
    run = [];
  };
  for (const block of blocks) {
    if (block.kind === "row") {
      run.push(block);
      continue;
    }
    flush();
    grouped.push(<React.Fragment key={block.key}>{block.node}</React.Fragment>);
  }
  flush();

  return <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pt-1 pb-3 [&>*]:shrink-0'>{grouped}</div>;
}
